import { Search, Moon, Sun, Siren, Activity, Command, Menu, LogOut } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIncidentList } from '@/api/incidents';
import { useMe, useLogout } from '@/api/auth';
import { useUI } from '@/lib/store';
import { cn } from '@/lib/cn';

export function Topbar() {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof document === 'undefined') return 'light';
    return (document.documentElement.getAttribute('data-theme') as 'light' | 'dark') || 'light';
  });
  const togglePalette = useUI(s => s.togglePalette);
  const toggleActivity = useUI(s => s.toggleActivity);
  const toggleNav = useUI(s => s.toggleNav);
  const nav = useNavigate();
  const { data: me } = useMe();
  const logout = useLogout();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('nv:theme', theme);
    } catch (_) {}
  }, [theme]);

  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const { data: incList = [] } = useIncidentList();
  const openIncidents = incList.filter(i => i.status !== 'resolved').length;

  async function handleLogout() {
    await logout.mutateAsync();
    nav('/login', { replace: true });
  }

  const initials = (me?.name || me?.email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() ?? '')
    .join('') || '?';

  return (
    <header className="h-12 shrink-0 bg-bg-elevated border-b border-border px-4 md:px-6 lg:px-8 flex items-center gap-2 md:gap-3">
      <button
        onClick={toggleNav}
        className="md:hidden w-8 h-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors"
        aria-label="Open menu"
      >
        <Menu size={16} />
      </button>
      <div className="flex-1 max-w-md relative">
        <button
          onClick={togglePalette}
          className="w-full h-7 pl-7 pr-12 rounded-md bg-surface-2 border border-transparent text-sm text-fg-subtle text-left hover:border-border focus:border-accent focus:outline-none transition-colors flex items-center"
        >
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
          Search tenants, workspaces, commands…
          <kbd className="absolute right-2 top-1/2 -translate-y-1/2 text-2xs text-fg-subtle font-mono bg-bg border border-border rounded px-1 h-4 inline-flex items-center gap-0.5">
            <Command size={9} /> K
          </kbd>
        </button>
      </div>

      {openIncidents > 0 && (
        <Link
          to="/incidents"
          className="ml-auto inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-[color:var(--warn-soft)] text-[color:var(--warn)] text-xs font-semibold hover:brightness-110"
        >
          <Siren size={13} />
          {openIncidents} active
        </Link>
      )}

      <div className={openIncidents > 0 ? 'flex items-center gap-1' : 'ml-auto flex items-center gap-1'}>
        <button
          onClick={toggleActivity}
          className="w-7 h-7 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors"
          aria-label="Activity (shift+A)"
          title="Activity (shift+A)"
        >
          <Activity size={14} />
        </button>
        <button
          onClick={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))}
          className="w-7 h-7 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
        </button>

        <div ref={menuRef} className="relative ml-1 pl-3 border-l border-border">
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="flex items-center gap-2 hover:bg-surface-2 rounded-md p-1 -m-1 transition-colors"
          >
            <div className="text-right hidden md:block leading-tight">
              <div className="text-xs font-semibold text-fg max-w-[140px] truncate">
                {me?.name || me?.email || '—'}
              </div>
              <div className="text-2xs text-fg-subtle capitalize">
                {me?.role ? roleLabel(me.role) : ''}
              </div>
            </div>
            {me?.picture ? (
              <img
                src={me.picture}
                alt={me.name || me.email}
                className="w-7 h-7 rounded-full border border-border"
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-surface-2 border border-border flex items-center justify-center text-xs font-semibold text-fg">
                {initials}
              </div>
            )}
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1.5 w-56 bg-bg-elevated border border-border rounded-md shadow-pop overflow-hidden z-50">
              <div className="px-3 py-2.5 border-b border-border">
                <div className="text-sm font-semibold text-fg truncate">{me?.name || '—'}</div>
                <div className="text-2xs text-fg-subtle truncate">{me?.email}</div>
              </div>
              <button
                onClick={handleLogout}
                disabled={logout.isPending}
                className={cn(
                  'w-full px-3 py-2 text-left text-sm inline-flex items-center gap-2 transition-colors',
                  'text-fg-muted hover:text-fg hover:bg-surface-2',
                )}
              >
                <LogOut size={13} />
                {logout.isPending ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function roleLabel(role: string): string {
  switch (role) {
    case 'platform_admin': return 'Platform admin';
    case 'sre': return 'SRE';
    case 'support': return 'Support';
    case 'billing': return 'Billing';
    case 'read_only': return 'Read-only';
    default: return role;
  }
}
